import React, { useEffect, useState } from "react";

import { useComponentStateCallback } from "hooks/dashboard/useComponentStateCallback";
import { DEFAULT_DATE_TIME_PATTERN } from "utils/constants";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { useGetDataInfoRange, useGetRecentData } from "api/api-hooks/datasets-hooks";
import { Dialog } from "primereact/dialog";
import { Instant, ZonedDateTime, ZoneOffset } from "@js-joda/core";
import useTimeState from "hooks/dashboard/useTimeState";
import { Tooltip } from "primereact/tooltip";
import { I, T } from "services/lang/LanguageService";
import { formatDateTime } from "utils/time-utils";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import {
  DataDisplayType,
  DiffType,
  DiffValueOptions,
  ValueChangeIndicatorProps,
} from "./ValueChangeIndicatorTypes";
import {
  defaultFromDateTime,
  defaultToDateTime,
  formatNumber,
  getTextColor,
} from "../charts/utils";
import HorizontalBarChart from "./HorizontalBarChart";
import {
  getDiffClassName,
  getLastFinishedMonthYearComparison,
  getMainClassName,
  getTitleClassName,
  getUpToPeriodLabel,
} from "./ValueChangeIndicatorUtils";
import "./ValueChangeIndicator.scss";
import { defaultLoadConf } from "../data-input-output-table/defaultConfigs";
import ValueContainer from "./ValueContainer";

export default function ValueChangeIndicator(props: ValueChangeIndicatorProps) {
  const setStatus = useComponentStateCallback(props);

  const [dialog, setDialog] = useState(false);
  const [currentDiffValueOptions, setCurrentDiffValueOptions] = useState<DiffValueOptions>({
    value: 0,
    currentValueInfo: "",
    previousValueInfo: "",
    isHorizontalBarChartDiffLoaded: false,
  });

  const timeState = useTimeState({
    from: props.from,
    to: props.to,
  });

  const defaultFromDate =
    timeState.from ??
    defaultFromDateTime({
      loadConf: { ...defaultLoadConf, ...props.loadConf },
      referenceTime: props.referenceTime,
    });

  const defaultToDate =
    timeState.to ??
    defaultToDateTime({
      loadConf: {
        ...defaultLoadConf,
        ...props.loadConf,
        alignUnit: props.useDataUpToLastFinishedMonth ? "MONTHS" : props.loadConf?.alignUnit,
        leadUnit: props.useDataUpToLastFinishedMonth ? "MINUTES" : props.loadConf?.leadUnit,
        lead: props.useDataUpToLastFinishedMonth ? 0 : props.loadConf?.lead,
      },
      referenceTime: props.referenceTime,
    });
  let refTime: string = defaultToDate.toString();
  if (props.useDataUpToLastFinishedMonth && defaultToDate) {
    const zdt = ZonedDateTime.ofInstant(
      Instant.ofEpochMilli(defaultToDate.getTime()),
      ZoneOffset.UTC
    );
    refTime = zdt.minusMinutes(2).toInstant().toString();
  }

  const { data, isLoading, error } = useGetRecentData(props.datasets, {
    count: 2,
    refTime,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled:
        props.dataDisplayType === DataDisplayType.LAST_VALUE || props.dataDisplayType === undefined,
    },
  });

  const {
    data: dataRange,
    isLoading: isLoadingRange,
    error: errorRange,
  } = useGetDataInfoRange(
    props.datasets,
    {
      timeStart: defaultFromDate?.toISOString(),
      timeEnd: defaultToDate?.toISOString(),
    },
    {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
      enabled:
        props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ||
        props.dataDisplayType === DataDisplayType.AVERAGE_VALUE ||
        props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON,
    }
  );

  const decimalPlaces = props.decimalPlaces || data?.[0]?.decimalPlaces;
  const diffDecimalPlaces = props.diffValueDecimalPlaces ?? decimalPlaces;
  const currentDataset = data?.[0] ?? dataRange?.[0];
  const currentData = currentDataset?.data;

  useEffect(() => {
    if (
      data &&
      currentData &&
      currentData.length >= 2 &&
      props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART
    ) {
      const newDiff: DiffValueOptions = {
        value: (currentData[1]?.value ?? 0) - (currentData[0]?.value ?? 0),
        currentValueInfo: renderDiffRow(
          "Current",
          currentData[1]?.value ?? 0,
          currentDataset?.unit ?? "",
          currentData[1]?.key ?? ""
        ),
        previousValueInfo: renderDiffRow(
          "Previous",
          currentData[0]?.value ?? 0,
          currentDataset?.unit ?? "",
          currentData[0]?.key ?? ""
        ),
      };
      setCurrentDiffValueOptions(newDiff);
    }
  }, [data, currentData]);

  useEffect(() => {
    if (
      props.reportMode &&
      ((!isLoading && data !== undefined) || (!isLoadingRange && dataRange !== undefined))
    ) {
      if (
        props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART &&
        !currentDiffValueOptions.isHorizontalBarChartDiffLoaded
      ) {
        return undefined;
      }
      return setStatus({ state: "finished" });
    }

    return undefined;
  }, [isLoading, isLoadingRange, currentDiffValueOptions]);

  if (error || errorRange) {
    const message = error?.message ?? errorRange?.message;
    easasAlert({
      summary: T("alert.failed-to-fetch-data"),
      detail: message,
      severity: "error",
    });
    setStatus({ state: "error", error: message });
    return null;
  }

  const formatDiff = (prev: number, curr: number, isNeutral?: boolean) => {
    if (!currentData) return "0";
    if (props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART) {
      if (isNeutral) {
        return "0";
      }

      if (props.diffType === DiffType.PERCENTAGE) {
        const percent = ((curr - prev) / prev) * 100;
        return `${percent.toFixed(diffDecimalPlaces)}%`;
      }
      return `${(curr - prev).toFixed(diffDecimalPlaces)} ${currentDataset?.unit}`;
    }
    return `${currentDiffValueOptions.value?.toFixed(diffDecimalPlaces)} ${props.diffType === DiffType.PERCENTAGE ? "%" : currentDataset?.unit}`;
  };

  const renderDiffRow = (label: string, value: number, unit: string, timestamp: string) => (
    <div className="flex flex-row justify-content-between gap-2">
      <b>{label}:</b>
      <span>
        {value.toFixed(diffDecimalPlaces)} {unit},{" "}
        {formatDateTime(timestamp, DEFAULT_DATE_TIME_PATTERN)}
      </span>
    </div>
  );

  const renderDatasetValue = (dataset: DatasetDataInfoResponse) => {
    const safeId = dataset.identifier.replace(/[^a-zA-Z0-9-_]/g, "_");
    const tooltipId = `last-value-${safeId}-tooltip`;
    const val = dataset.data?.[1];
    const isMultiple = props.datasets.length > 1 && !props.displayInline;
    const textColor = props.highlightValue ? "" : getTextColor(props.theme);
    const datasetValueClassName = `value-container${isMultiple ? "-multiple" : ""} flex flex-row gap-1 align-items-end ${props.highlightValue ? "highlight-value" : ""}`;

    if (props.hideZeroValues && val?.value === 0) {
      return null;
    }

    return (
      <React.Fragment key={dataset.identifier}>
        <div
          id={tooltipId}
          style={{ color: textColor }}
          className={datasetValueClassName}
          onDoubleClick={() => setDialog(true)}
        >
          <div className="value cursor-pointer font-bold">
            {formatNumber(val.value, decimalPlaces, props.convertToAbsoluteValues)}
          </div>
          <div className="unit cursor-pointer font-bold">{dataset.unit}</div>
        </div>

        <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
          {val ? (
            <div>{renderDiffRow(T("word.current-value"), val.value, dataset.unit, val.key)}</div>
          ) : (
            T("word.no-data")
          )}
        </Tooltip>
      </React.Fragment>
    );
  };

  const getDiffTooltip = () => {
    if (props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART) {
      return (
        <div className="flex flex-column gap-1">
          <div>{currentDiffValueOptions.previousValueInfo}</div>
          <div>{currentDiffValueOptions.currentValueInfo}</div>
        </div>
      );
    }
    if (currentData?.[0] && currentData?.[1] && currentDataset) {
      return (
        <div className="flex flex-column gap-1">
          {renderDiffRow(
            T("word.previous-value"),
            currentData[0].value,
            currentDataset.unit,
            currentData[0].key
          )}
          {renderDiffRow(
            T("word.current-value"),
            currentData[1].value,
            currentDataset.unit,
            currentData[1].key
          )}
        </div>
      );
    }
    return <div>{T("word.no-data")}</div>;
  };

  const getComponentWidth = () => {
    if (props.reportMode) {
      return `${props.width}px` || "100%";
    }
    return "100%";
  };

  const getItemsForAverageValues = (currentDataRange: DatasetDataInfoResponse[]) =>
    currentDataRange.map(ds => {
      const values = (ds.data ?? [])
        .map(p => p?.value)
        .filter((v): v is number => Number.isFinite(v));
      const count = values.length;
      const avg = count ? values.reduce((a, b) => a + b, 0) / count : null;
      return { ds, avg, count };
    });

  const titleAndSubtitleTemplate = () => {
    return (
      <div className="flex flex-column title-container">
        <div
          style={{ color: getTextColor(props.theme) }}
          className={getTitleClassName(props.dataDisplayType, props.displayInline)}
        >
          <div>{I(props.title ?? "")}</div>
        </div>
        <div style={{ color: getTextColor(props.theme) }} className="subtitle font-medium">
          {props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART && props.upToUnit ? (
            <div>
              {T("label.up-to")} {getUpToPeriodLabel(props.upToUnit, props.referenceTime)}
            </div>
          ) : (
            <div>{I(props.subtitle ?? "")}</div>
          )}
        </div>
      </div>
    );
  };

  const diffValueForLastValue = () => {
    if (
      props.displayLastValueDiff &&
      props.dataDisplayType !== DataDisplayType.AVERAGE_VALUE &&
      props.dataDisplayType !== DataDisplayType.MONTH_YEAR_COMPARISON &&
      currentData
    ) {
      const prev = currentData[0]?.value;
      const curr = currentData[1]?.value;
      const diffValue = currentDiffValueOptions.value ?? curr - prev;
      const isNegative = diffValue < 0;
      const isNeutral = Number(diffValue.toFixed(props.diffValueDecimalPlaces)) === 0;
      const diffClass = isNegative ? "negative" : "positive";
      const arrowDirection = isNegative ? "down" : "up";
      const safeDiffId = data?.[0]?.identifier?.replace(/[^a-zA-Z0-9-_]/g, "_") ?? "default";
      const diffTooltipId = `last-value-diff-${safeDiffId}-tooltip`;
      return (
        <>
          <div
            id={diffTooltipId}
            className={getDiffClassName(
              isNeutral,
              props.dataDisplayType,
              props.useDiffIndicatorColors
            )}
          >
            <div className={diffClass}>{formatDiff(prev, curr, isNeutral)}</div>
            {isNeutral && <i className="pi pi-arrows-v" />}
            {!isNeutral && <i className={`pi pi-arrow-${arrowDirection} ${diffClass}`} />}
          </div>
          <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
            {getDiffTooltip()}
          </Tooltip>
        </>
      );
    }
    return null;
  };

  const diffValueForMonthYearComparison = () => {
    if (
      props.displayLastValueDiff &&
      props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON &&
      dataRange
    ) {
      const dataset = dataRange[0]?.data ?? [];
      const { prev, curr } = getLastFinishedMonthYearComparison(dataset);
      if (curr == null || prev == null) return null;

      const diffValue = currentDiffValueOptions.value || (curr ?? 0) - (prev ?? 0);
      const isNegative = diffValue < 0;
      const isNeutral = Number(diffValue.toFixed(props.diffValueDecimalPlaces)) === 0;
      const diffClass = isNegative ? "negative" : "positive";
      const arrowDirection = isNegative ? "down" : "up";
      const safeDiffId = data?.[0]?.identifier?.replace(/[^a-zA-Z0-9-_]/g, "_") ?? "default";
      const diffTooltipId = `last-value-diff-${safeDiffId}-tooltip`;
      return (
        <>
          <div
            id={diffTooltipId}
            className={getDiffClassName(
              isNeutral,
              props.dataDisplayType,
              props.useDiffIndicatorColors
            )}
          >
            <div className={diffClass}>{formatDiff(prev, curr, isNeutral)}</div>
            {isNeutral && <i className="pi pi-arrows-v" />}
            {!isNeutral && <i className={`pi pi-arrow-${arrowDirection} ${diffClass}`} />}
          </div>
          <Tooltip target={`#${diffTooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
            {getDiffTooltip()}
          </Tooltip>
        </>
      );
    }
    return null;
  };

  const lastValueTemplate = () => {
    if (
      data &&
      data.length === 1 &&
      (props.dataDisplayType === DataDisplayType.LAST_VALUE || props.dataDisplayType === undefined)
    ) {
      const dataset = data[0];
      const val = dataset.data?.[1];
      const safeId = dataset.identifier.replace(/[^a-zA-Z0-9-_]/g, "_");
      const tooltipId = `last-value-${safeId}-tooltip`;

      return (
        <ValueContainer
          id={tooltipId}
          value={formatNumber(val.value, decimalPlaces, props.convertToAbsoluteValues)}
          unit={!props.hideUnit ? dataset.unit : undefined}
          highlight={props.highlightValue}
          color={getTextColor(props.theme)}
          onDoubleClick={() => setDialog(true)}
        >
          <Tooltip target={`#${tooltipId}`} mouseTrack mouseTrackLeft={10} position="bottom">
            {val ? (
              <div>{renderDiffRow(T("word.current-value"), val.value, dataset.unit, val.key)}</div>
            ) : (
              T("word.no-data")
            )}
          </Tooltip>
        </ValueContainer>
      );
    }
    return null;
  };

  const lastValueTemplateForMonthYearComparison = () => {
    if (
      Array.isArray(dataRange) &&
      dataRange.length === 1 &&
      props.dataDisplayType === DataDisplayType.MONTH_YEAR_COMPARISON
    ) {
      const dataset = dataRange[0];
      let value: number;
      if (props.useDataUpToLastFinishedMonth) {
        const { curr } = getLastFinishedMonthYearComparison(dataset.data);
        value = curr !== null ? curr : 0;
      } else {
        value = dataset.data?.[1]?.value ?? 0;
      }
      const safeId = dataset.identifier.replace(/[^a-zA-Z0-9-_]/g, "_");
      const tooltipId = `last-value-${safeId}-tooltip`;

      return (
        <ValueContainer
          id={tooltipId}
          value={formatNumber(value, decimalPlaces, props.convertToAbsoluteValues)}
          unit={!props.hideUnit ? dataset.unit : undefined}
          highlight={props.highlightValue}
          color={getTextColor(props.theme)}
          onDoubleClick={() => setDialog(true)}
        />
      );
    }
    return null;
  };

  const lastValueTemplateForAverage = () => {
    if (
      Array.isArray(dataRange) &&
      dataRange.length > 0 &&
      props.dataDisplayType === DataDisplayType.AVERAGE_VALUE
    ) {
      const allUnits = new Set(dataRange.map(ds => ds.unit).filter(Boolean));
      const sameUnit = allUnits.size === 1 ? [...allUnits][0] : undefined;

      return (
        <div
          className={`avg-values-container flex flex-column gap-2 ${
            props.highlightValue ? "highlight-value" : ""
          }`}
          style={{ color: props.highlightValue ? "" : getTextColor(props.theme) }}
          onDoubleClick={() => setDialog(true)}
        >
          {getItemsForAverageValues(dataRange).map(({ ds, avg }) => {
            const dp = decimalPlaces ?? ds.decimalPlaces;
            const unitToShow = sameUnit ?? ds.unit;

            return (
              <ValueContainer
                key={ds.identifier}
                value={avg !== null ? formatNumber(avg, dp, props.convertToAbsoluteValues) : "–"}
                unit={!props.hideUnit ? unitToShow : undefined}
                color={getTextColor(props.theme)}
              />
            );
          })}
        </div>
      );
    }
    return null;
  };

  const flexDirectionClass = props.displayInline
    ? "flex-row justify-content-between"
    : "flex-column";

  const className = `${getMainClassName(props.dataDisplayType, props.reportMode, props.displayInline)} flex ${flexDirectionClass}`;
  return (
    <div style={{ width: getComponentWidth() }} className={className}>
      <div className="flex flex-row justify-content-between">
        {titleAndSubtitleTemplate()}
        {diffValueForLastValue()}
        {diffValueForMonthYearComparison()}
      </div>

      {lastValueTemplate()}
      {lastValueTemplateForMonthYearComparison()}
      {lastValueTemplateForAverage()}

      {props.dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART &&
        dataRange &&
        props.aggregationPeriod && (
          <div onDoubleClick={() => setDialog(true)}>
            <HorizontalBarChart
              data={dataRange[0]}
              diffType={props.diffType}
              setCurrentDiffValueOptions={setCurrentDiffValueOptions}
              theme={props.theme}
              reportMode={props.reportMode}
              decimalPlaces={decimalPlaces}
            />
          </div>
        )}

      {data &&
        data?.length > 1 &&
        !props.displayLastValueDiff &&
        props.dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART &&
        props.dataDisplayType !== DataDisplayType.AVERAGE_VALUE && (
          <div
            className={`value-column ${props.displayInline ? "inline-values" : "column-values mt-2"} `}
          >
            {props.hideIfValueIsZeroOrMore && data[0].data[0].value >= 0
              ? null
              : data.map(renderDatasetValue)}
          </div>
        )}

      <Dialog
        header={T("label.scada-tags-chart")}
        visible={dialog}
        style={{ width: "min(100vw, 1000px)", height: "500px" }}
        modal={false}
        onHide={() => setDialog(false)}
      >
        <DatasetLineChart
          title=""
          identifiers={props.datasets}
          showDateSelection
          dynamicHeight
          chartDialogOpen={dialog}
        />
      </Dialog>
    </div>
  );
}
